/**
 * Draft state for the scenario editor, and the conversion in and out of the
 * API's `ScenarioPayload`.
 *
 * The editor works on a flat `turns` array (index 0 is the opener) because
 * that's what the UI manipulates — add, remove, reorder. The API splits the
 * same thing into `question` + `followUps`. Keeping the translation here means
 * neither side has to know about the other's shape, and it's testable without
 * rendering anything.
 */
import {
  ASSERTION_KIND_META,
  assertionNeedsValue,
  type ScenarioAssertion,
  type ScenarioCaller,
  type ScenarioChannel,
  type ScenarioCustomer,
  type ScenarioMode,
  type ScenarioPayload,
  type ScenarioPersona,
} from './batch-tests';

/** What the editor edits, before it becomes a ScenarioPayload. */
export interface ScenarioDraft {
  name: string;
  /** Every customer turn in order; index 0 is the opener. Always ≥1 entry.
   *  In SIMULATED mode only turn 0 is sent; the rest are kept so switching
   *  back to SCRIPTED doesn't lose the script. */
  turns: string[];
  assertions: ScenarioAssertion[];
  mode: ScenarioMode;
  persona: ScenarioPersona;
  maxTurns: number;
  expectation: string;
  /** Seed a known caller before turn 1. All-blank = the caller is a stranger. */
  customer: ScenarioCustomerDraft;
  /** Arrive as an identity that already exists. Blank identifier = a minted one. */
  caller: ScenarioCallerDraft;
}

/**
 * The arriving-identity fields, held flat and always present for the same
 * reason as the fixture's: toggling the section off and on must not lose what
 * was picked.
 */
export interface ScenarioCallerDraft {
  channel: ScenarioChannel;
  /** Blank = no caller identity; the run mints its own and the caller is a stranger. */
  identifier: string;
}

/**
 * The seeded-caller fields, held flat and always present so toggling the
 * section off and on again doesn't lose what was typed.
 */
export interface ScenarioCustomerDraft {
  name: string;
  email: string;
  address: string;
  /** Blank = no membership; the caller exists but has no plan. */
  planId: string;
}

/** A blank draft: one empty opening turn, no assertions. */
export function emptyScenarioDraft(): ScenarioDraft {
  return {
    name: '',
    turns: [''],
    assertions: [],
    mode: 'SCRIPTED',
    persona: { description: '', goal: '' },
    maxTurns: 8,
    expectation: '',
    customer: { name: '', email: '', address: '', planId: '' },
    caller: { channel: 'sms', identifier: '' },
  };
}

/** Load an existing scenario into a draft. */
export function toScenarioDraft(scenario: {
  name: string | null;
  question: string;
  followUps: string[];
  assertions: ScenarioAssertion[];
  mode?: ScenarioMode;
  persona?: ScenarioPersona | null;
  maxTurns?: number;
  expectation?: string | null;
  customer?: ScenarioCustomer | null;
  caller?: ScenarioCaller | null;
}): ScenarioDraft {
  return {
    name: scenario.name ?? '',
    turns: [scenario.question, ...scenario.followUps],
    assertions: scenario.assertions,
    mode: scenario.mode ?? 'SCRIPTED',
    persona: scenario.persona ?? { description: '', goal: '' },
    maxTurns: scenario.maxTurns ?? 8,
    expectation: scenario.expectation ?? '',
    customer: {
      name: scenario.customer?.name ?? '',
      email: scenario.customer?.email ?? '',
      address: scenario.customer?.address ?? '',
      planId: scenario.customer?.membership?.planId ?? '',
    },
    caller: {
      channel: scenario.caller?.channel ?? 'sms',
      identifier: scenario.caller?.identifier ?? '',
    },
  };
}

/**
 * The arriving identity to send, or undefined when none was chosen.
 *
 * A blank identifier means the run mints its own — the channel alone carries no
 * identity, so sending it would claim a known caller the engine cannot resolve.
 */
export function callerFromDraft(draft: ScenarioCallerDraft): ScenarioCaller | undefined {
  const identifier = draft.identifier.trim();
  if (!identifier) return undefined;
  return { channel: draft.channel, identifier };
}

/**
 * The fixture to send, or undefined when nothing was filled in.
 *
 * An all-blank form must send NOTHING rather than an empty object: seeding a
 * nameless contact would make the scenario quietly different from the stranger
 * case it looks identical to.
 */
export function customerFromDraft(draft: ScenarioCustomerDraft): ScenarioCustomer | undefined {
  const name = draft.name.trim();
  const email = draft.email.trim();
  const address = draft.address.trim();
  const planId = draft.planId.trim();
  if (!name && !email && !address && !planId) return undefined;
  return {
    ...(name ? { name } : {}),
    ...(email ? { email } : {}),
    ...(address ? { address } : {}),
    ...(planId ? { membership: { planId } } : {}),
  };
}

/**
 * Convert a draft to an API payload, or return an error explaining why it can't
 * be sent yet. Empty turns are dropped rather than rejected — an operator who
 * adds a turn and changes their mind shouldn't hit a validation wall.
 */
export function draftToPayload(
  draft: ScenarioDraft,
): { payload: ScenarioPayload } | { error: string } {
  const turns = draft.turns.map((t) => t.trim()).filter(Boolean);
  if (turns.length === 0) return { error: 'Add what the customer says first.' };

  for (const assertion of draft.assertions) {
    if (assertionNeedsValue(assertion.kind) && !assertion.value?.trim()) {
      const meta = ASSERTION_KIND_META[assertion.kind];
      return { error: `"${meta.label}" needs a ${meta.valueLabel?.toLowerCase()}.` };
    }
  }

  // A SIMULATED scenario is only meaningful with a persona that has a goal —
  // without one the simulator cannot know when the conversation is finished.
  const simulated = draft.mode === 'SIMULATED';
  if (simulated && !(draft.persona.description.trim() && draft.persona.goal.trim())) {
    return { error: 'A simulated scenario needs both who the customer is and what they want.' };
  }

  // Naming an identity that exists and fabricating one contradict each other,
  // and the API rejects the pair for a sharper reason: the fixture is torn down
  // by the contact id it was given back, so seeding it onto an identifier that
  // already belongs to a customer can delete that customer's real record.
  if (callerFromDraft(draft.caller) && customerFromDraft(draft.customer)) {
    return {
      error:
        'Pick one: arrive as an existing customer, or seed a new one. A seeded customer would be created on, and then removed from, that caller’s own record.',
    };
  }

  const [question, ...followUps] = turns;
  return {
    payload: {
      ...(draft.name.trim() ? { name: draft.name.trim() } : {}),
      question: question as string,
      followUps,
      assertions: draft.assertions.map((a) => ({
        kind: a.kind,
        ...(a.value?.trim() ? { value: a.value.trim() } : {}),
      })),
      mode: draft.mode,
      ...(simulated
        ? {
            persona: {
              description: draft.persona.description.trim(),
              goal: draft.persona.goal.trim(),
              ...(draft.persona.tone?.trim() ? { tone: draft.persona.tone.trim() } : {}),
              ...(draft.persona.language?.trim()
                ? { language: draft.persona.language.trim() }
                : {}),
            },
            maxTurns: draft.maxTurns,
          }
        : {}),
      ...(draft.expectation.trim() ? { expectation: draft.expectation.trim() } : {}),
      // ALWAYS sent, explicitly null when blank — never omitted. On the update
      // route an omitted field means "leave it alone", so clearing the caller
      // fields would look cleared in the editor while the next run still
      // executed as the old known customer: the UI and the behaviour would
      // disagree with nothing on screen to explain it.
      customer: customerFromDraft(draft.customer) ?? null,
      // Always sent, explicitly null when blank — same reason as `customer`.
      caller: callerFromDraft(draft.caller) ?? null,
    },
  };
}
